import useRouter from 'packages/hooks/use-router';
import {useFormContext} from 'react-hook-form';
import {useState} from 'react';
import TextField from '@material-ui/core/TextField/TextField';
import {REQUIRED_FIELD_MESSAGE} from 'packages/constants/constants';
import FormControlLabel from '@material-ui/core/FormControlLabel/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import Button from '@material-ui/core/Button/Button';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {signInUser} from 'services/firebase-service';
import {Redirect} from 'react-router-dom';
import {Box} from '@material-ui/core';
import useAppAuth from 'packages/hooks/use-app-auth';

type ILoginForm = {
  emailInput: string;
  passwordInput: string;
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100vh',
  },
  image: {
    background: 'linear-gradient(26.82deg, #39C6C6 -24.85%, #2C9138 98.25%)',
  },
  smallText: {
    fontSize: '0.85rem',
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

const LoginForm = () => {
  const classes = useStyles();
  const {register, handleSubmit, errors, reset} = useFormContext<ILoginForm>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const {history} = useRouter();
  const auth = useAppAuth();

  const onSubmit = async (formData: ILoginForm) => {
    const {emailInput, passwordInput} = formData;
    setLoading(true);
    try {
      await signInUser({emailInput, passwordInput});
      if (auth.user) {
        history.replace(`/app`);
      }
      reset();
    } catch (err) {
      console.error(err);
      const message = (err as Error).message as string;
      setError(message);
    }
    setLoading(false);
  };

  const handleForgotPassword = () => {
    history.push(`/password-reset`);
  };

  if (auth?.authenticated && auth.isAdmin) return <Redirect to="/admin" />;
  if (auth?.authenticated && !auth.isAdmin) return <Redirect to="/app" />;

  return (
    <>
      {error && (
        <Alert id="error-alert" severity="error">
          {error}
        </Alert>
      )}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={classes.form}
        noValidate
      >
        <TextField
          error={errors.emailInput && true}
          variant="outlined"
          margin="normal"
          required
          fullWidth
          inputRef={register({
            required: {
              value: true,
              message: REQUIRED_FIELD_MESSAGE,
            },
          })}
          id="emailInput"
          label="Email Address"
          name="emailInput"
          autoComplete="email"
          autoFocus
          helperText={errors.emailInput ? errors.emailInput.message : ''}
        />
        <TextField
          error={!!errors.passwordInput}
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="passwordInput"
          inputRef={register({
            required: {
              value: true,
              message: REQUIRED_FIELD_MESSAGE,
            },
          })}
          label="Password"
          type="password"
          id="passwordInput"
          autoComplete="current-password"
          helperText={errors.passwordInput ? errors.passwordInput.message : ''}
        />
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <FormControlLabel
            className={classes.smallText}
            control={<Checkbox value="remember" color="primary" />}
            label="Recuerdame"
          />
          <Button name="btn-reset-password" onClick={handleForgotPassword}>
            Olvidé mi contraseña
          </Button>
        </Box>

        <Button
          type="submit"
          fullWidth
          size="medium"
          disableElevation
          disabled={loading}
          name="login-btn"
          variant="contained"
          color="primary"
          className={classes.submit}
        >
          {loading ? (
            <CircularProgress color="secondary" size={24} />
          ) : (
            `Ingresar`
          )}
        </Button>
      </form>
    </>
  );
};

export default LoginForm;
